import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { kioskCommand } from 'api/Kiosk';

import { useCustomMutation } from 'lib/hooks/useCustomMutation';

import Button from 'components/shared/Button/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Icon from 'components/shared/Icon/Icon';
import Allowed from 'components/shared/Permissions/Allowed';
import Skeleton from 'components/shared/Skeleton/Skeleton';

import CollapsibleSection from './components/CollapsibleSection';
import Field from './components/Field';
import SummaryRow from './components/SummaryRow';
import { SECTIONS } from './lib/sections';

const RETRY_AFTER_MS = 6000;

const TerminalDeviceInfo = ({ kioskId, info }) => {
  const navigate = useNavigate();
  const [updatedAt, setUpdatedAt] = useState(null);
  const [hasError, setHasError] = useState(false);
  const retryTimerRef = useRef(null);

  const { mutate: requestInfo, isPending } = useCustomMutation({
    mutationFn: () => kioskCommand(navigate, { command: 'payroc-get-terminal-info', kioskId }),
    disableAlert: true,
    onSuccess: () => setHasError(false),
    handleResponse: () => setHasError(true)
  });

  const clearRetry = () => {
    if (retryTimerRef.current) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  };

  const scheduleRetry = () => {
    clearRetry();
    retryTimerRef.current = setTimeout(() => {
      retryTimerRef.current = null;
      requestInfo();
    }, RETRY_AFTER_MS);
  };

  useEffect(() => {
    if (!info) return;
    setUpdatedAt(Date.now());
    setHasError(false);
    clearRetry();
  }, [info]);

  useEffect(() => {
    if (!kioskId) return undefined;
    requestInfo();
    scheduleRetry();
    return () => clearRetry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kioskId]);

  const handleRefresh = () => {
    requestInfo();
    scheduleRetry();
  };

  const showSkeleton = isPending && !info;

  return (
    <div className="flex flex-col gap-3 px-5">
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <p className="font-500 text-neutral-900">Terminal device info</p>
          <p className="text-sm text-neutral-700">
            Live diagnostics from the paired payment terminal.
            {updatedAt
              ? ` Updated ${new Date(updatedAt).toLocaleTimeString()}.`
              : ' Refresh to request the latest values.'}
          </p>
        </div>
        <Button
          variant="tertiary"
          size="small"
          icon="refresh-mono"
          iconColor="neutral"
          text="Refresh"
          onClick={handleRefresh}
          isDisabled={isPending}
          loading={isPending}
        />
      </div>

      {showSkeleton ? (
        <Skeleton height="120px" />
      ) : !info ? (
        <div className="flex flex-col items-center justify-center gap-1 rounded-md border border-dashed border-neutral-200 bg-white py-6">
          <Icon icon="info-circle-mono" size={20} color="neutral" />
          <p className="text-sm text-neutral-700">
            {hasError
              ? 'Could not reach the terminal. Make sure the kiosk is online and try again.'
              : 'Waiting for the kiosk to report terminal info…'}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <SummaryRow device={info} />
          {SECTIONS.map((section) => (
            <Allowed key={section.title} requiredPermissions={section?.permission}>
              <CollapsibleSection key={section.title} title={section.title}>
                <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
                  {section.fields.map((field) => (
                    <Field
                      key={field.key}
                      label={field.label}
                      value={info[field.key]}
                      format={field.format}
                    />
                  ))}
                </div>
              </CollapsibleSection>
            </Allowed>
          ))}
        </div>
      )}
    </div>
  );
};

export default withErrorBoundary(TerminalDeviceInfo);
